import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import ConnectionStatus from './ConnectionStatus.jsx'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import { textRenderer } from 'handsontable/renderers'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { Modal } from 'bootstrap'
import { cable } from '../../cable.js'
import { normalizeNumericValue } from '../utils/numberFormat.js'
import { showSuccess, showError } from '../utils/notifications.js'
import { handleHotKeyboardShortcut } from '../utils/hotKeyboardShortcuts.js'
import usePasteOverflow from '../hooks/usePasteOverflow.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { HOT_DATE_FORMAT, HOT_DATE_LOCALE, hotNumericFormat } from '../utils/handsontableConfig.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'
import { NativeDateEditor } from '../utils/nativeDateEditor.js'

// Register all Handsontable modules
registerAllModules()

// Source tracking for changes
const SOURCE = {
  EDIT: 'edit',
  SERVER: 'server',
  LOAD: 'loadData'
}

// Debounce delay for batch updates (ms)
const DEBOUNCE_DELAY = 800

// Numeric column props (for paste/input normalization)
const NUMERIC_PROPS = new Set(['area', 'current_rent', 'annual_rent', 'lease_break_days', 'rent_free_days', 'market_rent_rate', 'market_rent_pa'])

// Market Rent fields — routed to /batch_update_market_rent instead of /batch_update.
// The MR endpoint knows about cross-field invariants (auto-clear unit on source=MLA,
// auto-create Segment 1 when switching to AREA_SCHEDULE, etc.) that generic Update doesn't.
const MR_FIELDS = new Set(['market_rent_source', 'market_rent_unit', 'market_rent_rate', 'market_rent_notes'])

export default function TenantsTable({
  valuationId,
  indexUrl,
  batchUpdateUrl,
  batchUpdateMarketRentUrl,
  batchCreateUrl,
  batchDestroyUrl,
  leaseBreaksUrlTemplate, // e.g., "/my/tenants/:id/tenant_lease_breaks"
  mlaModalUrlTemplate, // e.g., "/my/tenants/:id/mla_modal"
  marketRentSegmentsUrlTemplate, // e.g., "/my/tenants/:id/market_rent_segments"
  rentReviewsUrlTemplate, // e.g., "/my/tenants/:id/rent_review_inputs/replace_all"
  valuationDate, // ISO date string for valuation_date guardrail in rent reviews modal
  csrfToken,
  translations = {},
  readOnly = false
}) {
  const hotRef = useRef(null)
  const viewportRef = useRef(null)
  const idToRowRef = useRef(new Map())
  const dataRef = useRef([])
  const segmentsCacheRef = useRef(new Map()) // tenantId -> latest market_rent_segments (kept as ref to avoid reloading Handsontable)
  const reviewsCacheRef = useRef(new Map()) // tenantId -> latest rent_review_inputs (relation pushed via TenantSerializer force_include)
  const pendingRef = useRef(new Map())
  const currentBatchIdRef = useRef(null)
  const flushTimeoutRef = useRef(null)
  const flushRef = useRef(null)
  const subscriptionRef = useRef(null)
  const handleServerPushRef = useRef(null)

  const [data, setData] = useState([])
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [selectedRowIds, setSelectedRowIds] = useState([])
  const [hotHeight, setHotHeight] = useState(null)
  const [connectionMountEl, setConnectionMountEl] = useState(null)

  const activeLabel = translations.active || 'Active'
  const noActiveLabel = translations.noActive || 'No Active'

  // Custom renderer for clickable lease break / rent free cells.
  // Note: for Rent Free we use a computed boolean `rent_free_active` (depends on valuation date),
  // not the numeric `rent_free_days`.
  // Renderer for the Rent Reviews summary cell — same clickable-link styling as lease break cells.
  // Cached count is taken from reviewsCacheRef (relation pushed via ActionCable broadcast).
  const rentReviewsCellRenderer = useCallback((instance, td, row, col, prop, value, cellProperties) => {
    const tenantId = instance.getDataAtRowProp(row, 'id')
    const list = (tenantId && reviewsCacheRef.current.get(tenantId)) || []
    const count = Array.isArray(list) ? list.length : 0
    const label = count === 0 ? 'None' : count === 1 ? '1 review' : `${count} reviews`

    textRenderer.call(this, instance, td, row, col, prop, label, cellProperties)
    td.style.cursor = 'pointer'
    td.style.color = '#0d6efd'
    td.style.textDecoration = 'underline'
    td.classList.add('rent-reviews-cell')
    if (tenantId) {
      td.dataset.tenantId = tenantId
      td.dataset.rentReviewsCell = 'true'
    }
  }, [])

  // EVA-137: Occupancy is computed + read-only. When the server couldn't
  // determine it confidently (incomplete/contradictory data) the row carries
  // `occupancy_warning` — show the value plus a warning marker + tooltip.
  const occupancyRenderer = useCallback((instance, td, row, col, prop, value, cellProperties) => {
    textRenderer.call(this, instance, td, row, col, prop, value, cellProperties)
    if (instance.getDataAtRowProp(row, 'occupancy_warning')) {
      td.classList.add('occupancy-warning-cell')
      td.title = translations.occupancyWarning ||
        'Occupancy could not be determined — check the lease status and lease dates.'
      const icon = document.createElement('span')
      icon.className = 'occupancy-warning-icon'
      icon.textContent = ' ⚠'
      td.appendChild(icon)
    }
  }, [translations])

  const leaseBreakCellRenderer = useCallback((instance, td, row, col, prop, _value, cellProperties) => {
    let isActive = false

    if (prop === 'rent_free_days') {
      isActive = Boolean(instance.getDataAtRowProp(row, 'rent_free_active'))
    } else {
      const rawValue = instance.getSourceDataAtCell(instance.toPhysicalRow(row), col)
      const n = rawValue === null || rawValue === undefined || rawValue === '' ? 0 : Number(rawValue)
      isActive = Number.isFinite(n) && n > 0
    }

    const label = isActive ? activeLabel : noActiveLabel

    // Render as text (we still keep the numeric value in data for sorting/filtering)
    textRenderer.call(this, instance, td, row, col, prop, label, cellProperties)

    // Style as clickable link
    td.style.cursor = 'pointer'
    td.style.color = '#0d6efd'
    td.style.textDecoration = 'underline'
    td.classList.add('lease-break-cell')

    // Store data attributes for click handler
    const tenantId = instance.getDataAtRowProp(row, 'id')
    if (tenantId) {
      td.dataset.tenantId = tenantId
      td.dataset.kind = prop === 'lease_break_days' ? 'BREAK' : 'RENT_FREE'
    }
  }, [activeLabel, noActiveLabel])

  const DATE_FIELDS = ['lease_start_date', 'lease_end_date', 'applied_lease_end_date', 'next_rent_review_date']

  // Enum fields that need value<->label conversion
  const ENUM_FIELDS = ['occupancy', 'lease_status', 'market_leasing_assumption_id', 'indexation_profile_id', 'current_rent_unit', 'payment_schedule', 'market_rent_source', 'market_rent_unit']
  
  const parseDisplayDateToISO = useCallback((value) => {
    if (value === null || value === undefined) return value

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const yyyy = String(value.getFullYear())
      const mm = String(value.getMonth() + 1).padStart(2, '0')
      const dd = String(value.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    }

    if (typeof value !== 'string') return value

    const trimmed = value.trim()
    if (trimmed === '') return ''

    // Already ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed

    // dd/mm/yyyy
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (slashMatch) {
      const [, d, m, y] = slashMatch
      const dd = String(d).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      return `${y}-${mm}-${dd}`
    }

    // dd-mmm-yy (e.g. 12-Feb-26)
    const mmmMatch = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/)
    if (mmmMatch) {
      const [, d, mon, yy] = mmmMatch
      const months = {
        jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
        jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12'
      }
      const mm = months[mon.toLowerCase()]
      if (!mm) return trimmed
      const dd = String(d).padStart(2, '0')
      const yyyy = String(2000 + parseInt(yy, 10))
      return `${yyyy}-${mm}-${dd}`
    }

    return trimmed
  }, [])

  // Build value<->label mappings from meta
  const enumMappings = React.useMemo(() => {
    const mappings = {}
    const metaKeys = {
      occupancy: 'occupancies',
      lease_status: 'lease_statuses',
      market_leasing_assumption_id: 'mlas',
      indexation_profile_id: 'indexation_profiles',
      current_rent_unit: 'rent_units',
      payment_schedule: 'payment_schedules',
      market_rent_source: 'market_rent_sources',
      market_rent_unit: 'market_rent_units'
    }
    
    for (const [field, metaKey] of Object.entries(metaKeys)) {
      const options = meta[metaKey] || []
      mappings[field] = {
        valueToLabel: Object.fromEntries(options.map(o => [o.value, o.label])),
        labelToValue: Object.fromEntries(options.map(o => [o.label, o.value])),
        labels: options.map(o => o.label)
      }
    }
    return mappings
  }, [meta])

  // Convert row data: value -> label for display
  const convertRowToDisplay = useCallback((row) => {
    const converted = { ...row }
    for (const field of ENUM_FIELDS) {
      if (converted[field] && enumMappings[field]) {
        converted[field] = enumMappings[field].valueToLabel[converted[field]] || converted[field]
      }
    }

    return converted
  }, [enumMappings])

  // FK-based enum fields: empty string should be sent as null
  const FK_ENUM_FIELDS = new Set(['market_leasing_assumption_id', 'indexation_profile_id'])

  // Convert changes: label -> value for saving
  const convertChangesToServer = useCallback((changes) => {
    const converted = { ...changes }
    for (const field of ENUM_FIELDS) {
      if (converted[field] !== undefined && enumMappings[field]) {
        const labelToValue = enumMappings[field].labelToValue
        const resolved = labelToValue[converted[field]]
        if (FK_ENUM_FIELDS.has(field)) {
          converted[field] = resolved ?? null
        } else {
          converted[field] = resolved || converted[field]
        }
      }
    }

    for (const field of DATE_FIELDS) {
      if (converted[field] !== undefined) {
        converted[field] = parseDisplayDateToISO(converted[field])
      }
    }

    return converted
  }, [enumMappings, parseDisplayDateToISO])

  // Column definitions (dropdown sources use translated labels)
  // Note: we keep `id` in row objects for syncing, but don't render it as a column.
  const columns = [
    { data: 'active', type: 'checkbox', width: 60, className: 'htCenter htMiddle' },
    { data: 'title', type: 'text', width: 200, className: 'htLeft htMiddle' },
    { data: 'suite', type: 'text', width: 100, className: 'htLeft htMiddle' },
    { data: 'usage', type: 'text', width: 150, className: 'htLeft htMiddle' },
    {
      data: 'occupancy',
      readOnly: true, // EVA-137: system-calculated, not user-editable
      renderer: occupancyRenderer,
      width: 100,
      className: 'htLeft htMiddle'
    },
    {
      data: 'lease_status',
      type: 'dropdown',
      source: enumMappings.lease_status?.labels || [],
      width: 120,
      className: 'htLeft htMiddle'
    },
    { data: 'area', type: 'numeric', width: 100, ...hotNumericFormat(2, true), className: 'htRight htMiddle' },
    { data: 'lease_start_date', type: 'date', editor: NativeDateEditor, locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT, width: 120, allowInvalid: false, className: 'htRight htMiddle' },
    { data: 'lease_end_date', type: 'date', editor: NativeDateEditor, locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT, width: 120, allowInvalid: false, className: 'htRight htMiddle' },
    { data: 'lease_break_days', type: 'numeric', readOnly: true, width: 110, renderer: leaseBreakCellRenderer, className: 'htLeft htMiddle' },
    { data: 'rent_free_days', type: 'numeric', readOnly: true, width: 110, renderer: leaseBreakCellRenderer, className: 'htLeft htMiddle' },
    { data: 'applied_lease_end_date', type: 'date', locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT, readOnly: true, width: 140, className: 'htRight htMiddle' },
    {
      data: 'current_rent_unit',
      type: 'dropdown',
      source: enumMappings.current_rent_unit?.labels || [],
      width: 140,
      className: 'htLeft htMiddle'
    },
    {
      data: 'current_rent',
      type: 'numeric',
      width: 120,
      ...hotNumericFormat(2, true),
      className: 'htRight htMiddle',
      renderer(instance, td, row, col, prop, _value, cellProperties) {
        // EVA-171: with the Market Rent unit the annual rent is derived from
        // the Market Rent Growth projection, so the manual Rent input is moot.
        // readOnly is set in the renderer, not cells() — see EVA-110 note.
        const unitLabel = instance.getDataAtRowProp(row, 'current_rent_unit')
        const isMarketRent = unitLabel === (enumMappings.current_rent_unit?.valueToLabel?.MARKET_RENT || 'Market Rent')

        cellProperties.readOnly = readOnly || isMarketRent

        if (isMarketRent) {
          textRenderer.call(this, instance, td, row, col, prop, '—', cellProperties)
          td.style.color = '#6c757d'
          return
        }
        const rawValue = instance.getSourceDataAtCell(instance.toPhysicalRow(row), col)
        const numValue = rawValue === null || rawValue === undefined || rawValue === ''
          ? rawValue
          : Number(rawValue)
        const displayValue = Number.isFinite(numValue)
          ? numValue.toLocaleString('en-US', { maximumFractionDigits: 4 })
          : rawValue
        textRenderer.call(this, instance, td, row, col, prop, displayValue, cellProperties)
        if (!cellProperties.readOnly) td.classList.remove('htDimmed')
        td.style.color = ''
      }
    },
    { data: 'annual_rent', type: 'numeric', readOnly: true, width: 120, ...hotNumericFormat(2, true), className: 'htRight htMiddle' },
    {
      data: 'payment_schedule',
      type: 'dropdown',
      source: enumMappings.payment_schedule?.labels || [],
      width: 190,
      className: 'htLeft htMiddle'
    },
    {
      data: 'rent_reviews',
      type: 'text',
      readOnly: true,
      width: 110,
      renderer: rentReviewsCellRenderer,
      className: 'htLeft htMiddle'
    },
    {
      data: 'indexation_profile_id',
      type: 'dropdown',
      source: ['', ...(enumMappings.indexation_profile_id?.labels || [])],
      width: 160,
      className: 'htLeft htMiddle'
    },
    {
      data: 'next_rent_review_date',
      type: 'date',
      locale: HOT_DATE_LOCALE,
      dateFormat: HOT_DATE_FORMAT,
      readOnly: true,
      width: 140,
      className: 'htRight htMiddle'
    },
    {
      data: 'market_leasing_assumption_id',
      type: 'dropdown',
      source: ['', ...(enumMappings.market_leasing_assumption_id?.labels || [])],
      width: 180,
      className: 'htLeft htMiddle',
      renderer(instance, td, row, col, prop, value, cellProperties) {
        textRenderer(instance, td, row, col, prop, value, cellProperties)
        const btn = document.createElement('span')
        btn.className = 'mla-info-btn'
        btn.textContent = '\u2699'
        btn.style.cssText = 'cursor:pointer;margin-left:6px;opacity:0.5;font-size:14px;'
        btn.dataset.mlaInfo = 'true'
        td.appendChild(btn)
        td.style.display = 'flex'
        td.style.alignItems = 'center'
        td.style.justifyContent = 'space-between'
      }
    },
    {
      data: 'market_rent_source',
      type: 'dropdown',
      source: enumMappings.market_rent_source?.labels || [],
      width: 140,
      className: 'htLeft htMiddle'
    },
    {
      data: 'market_rent_unit',
      type: 'dropdown',
      source: enumMappings.market_rent_unit?.labels || [],
      width: 200,
      className: 'htLeft htMiddle',
      renderer(instance, td, row, col, prop, value, cellProperties) {
        const sourceLabel = instance.getDataAtRowProp(row, 'market_rent_source')
        const isArea = sourceLabel === 'MR Breakdown'
        const isMLA = sourceLabel === 'MLA'
        const isManual = sourceLabel === 'Manual'

        // cells() results are cached in metaSyncMemo and only invalidated on forceFullRender,
        // so set readOnly directly in the renderer where it runs every render (EVA-110).
        cellProperties.readOnly = readOnly || !isManual

        const displayValue = isArea
          ? (translations.fromAreaSchedule || 'From MR Breakdown')
          : isMLA
            ? (translations.fromMla || 'From MLA')
            : (value ?? '')

        textRenderer.call(this, instance, td, row, col, prop, displayValue, cellProperties)

        // baseRenderer adds htDimmed on readOnly but never removes it on toggle.
        if (!cellProperties.readOnly) td.classList.remove('htDimmed')

        // Reset inline styles leaked from a prior MLA/Area render on the reused <td>.
        td.style.color = ''
        td.style.fontStyle = ''
        td.style.display = ''
        td.style.alignItems = ''
        td.style.justifyContent = ''

        if (isMLA || isArea) {
          td.style.color = '#6c757d'
          td.style.fontStyle = 'italic'
        }

        if (isArea) {
          const btn = document.createElement('span')
          btn.className = 'area-schedule-edit-btn'
          btn.textContent = translations.edit || 'Edit'
          btn.style.cssText = 'cursor:pointer;margin-left:6px;color:#0d6efd;text-decoration:underline;font-size:12px;'
          btn.dataset.areaScheduleEdit = 'true'
          td.appendChild(btn)
          td.style.display = 'flex'
          td.style.alignItems = 'center'
          td.style.justifyContent = 'space-between'
        }
      }
    },
    {
      data: 'market_rent_rate',
      type: 'numeric',
      width: 100,
      ...hotNumericFormat(2, true),
      className: 'htRight htMiddle',
      renderer(instance, td, row, col, prop, _value, cellProperties) {
        const sourceLabel = instance.getDataAtRowProp(row, 'market_rent_source')
        const isManual = sourceLabel === 'Manual'

        cellProperties.readOnly = readOnly || !isManual

        if (!isManual) {
          textRenderer.call(this, instance, td, row, col, prop, '—', cellProperties)
          td.style.color = '#6c757d'
          return
        }
        // Numeric formatting (thousands separator) is declared in the column
        // schema, but textRenderer doesn't apply numericFormat — format manually
        // to match the prototype's `0,0.00`-style display (EVA-154).
        const rawValue = instance.getSourceDataAtCell(instance.toPhysicalRow(row), col)
        const numValue = rawValue === null || rawValue === undefined || rawValue === ''
          ? rawValue
          : Number(rawValue)
        const displayValue = Number.isFinite(numValue)
          ? numValue.toLocaleString('en-US', { maximumFractionDigits: 4 })
          : rawValue
        textRenderer.call(this, instance, td, row, col, prop, displayValue, cellProperties)
        if (!cellProperties.readOnly) td.classList.remove('htDimmed')
        td.style.color = ''
      }
    },
    {
      data: 'market_rent_pa',
      type: 'numeric',
      readOnly: true,
      width: 130,
      ...hotNumericFormat(2, true),
      className: 'htRight htMiddle'
    },
    {
      data: 'market_rent_notes',
      type: 'text',
      width: 200,
      className: 'htLeft htMiddle'
    }
  ]

  // Handsontable reads widths from `colWidths`, not from `columns[i].width`.
  // Keeping this explicit prevents column shrink/wrap (row height "jumping") on first render.
  const colWidths = React.useMemo(
    () => columns.map(c => (typeof c.width === 'number' ? c.width : undefined)),
    [columns]
  )

  const colHeaders = [
    'Active', 'Tenant Name', 'Suite', 'Usage', 'Occupancy', 'Lease Status',
    'Area', 'Lease Start', 'Lease End', 'Lease Breaks', 'Rent Free',
    'Applied Lease End', 'Rent Unit', 'Rent', 'Annual Rent', 'Payment Schedule',
    'Rent Reviews', 'Indexation', 'Next Rent Review',
    'MLA',
    'MR Source', 'MR Unit', 'MR Rate', 'MR pa', 'MR Notes'
  ]

  const nestedHeaders = React.useMemo(() => ([
    [
      { label: 'General', colspan: 7 },
      { label: 'Lease Dates', colspan: 5 },
      { label: 'Rental Income', colspan: 6 },
      { label: 'Market Rent', colspan: 5 }
    ],
    colHeaders
  ]), [colHeaders])

  const handleAfterGetColHeader = useCallback((col, TH, headerLevel) => {
    if (!TH) return

    // Group headers (colspan > 1) should be centered.
    if (TH.colSpan && TH.colSpan > 1) {
      TH.classList.add('htCenter', 'htMiddle')
      return
    }

    // For nested headers, `headerLevel` 0 is the group row, 1 is the column header row.
    // If `headerLevel` isn't provided by the wrapper/version, this still works due to colSpan check above.
    if (headerLevel === 0) {
      TH.classList.add('htCenter', 'htMiddle')
      return
    }

    if (col < 0) {
      // Top-left corner (row headers header).
      TH.classList.add('htRight', 'htMiddle')
      if (!TH.textContent || TH.textContent.trim() === '') TH.textContent = 'Number'
      return
    }

    const className = columns[col]?.className || ''
    className.split(' ').filter(Boolean).forEach(cls => TH.classList.add(cls))
  }, [columns])

  const handleAfterGetRowHeader = useCallback((row, TH) => {
    if (!TH) return
    TH.classList.add('htRight', 'htMiddle')
    if (row === -1 && (!TH.textContent || TH.textContent.trim() === '')) {
      TH.textContent = 'Number'
    }
  }, [])

  // Keep dataRef in sync with data state so event handlers can read the latest row details
  useEffect(() => {
    dataRef.current = data
    // Also seed relation caches from initial/refreshed data so panels/modals have something to read
    data.forEach(row => {
      if (row.id && Array.isArray(row.market_rent_segments)) {
        segmentsCacheRef.current.set(row.id, row.market_rent_segments)
      }
      if (row.id && Array.isArray(row.rent_review_inputs)) {
        reviewsCacheRef.current.set(row.id, row.rent_review_inputs)
      }
    })
  }, [data])

  // Rebuild id -> physical rowIndex mapping
  const rebuildIdMap = useCallback(() => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return

    const map = new Map()
    const sourceData = hot.getSourceData()
    sourceData.forEach((row, index) => {
      if (row.id) map.set(row.id, index)
    })
    idToRowRef.current = map
  }, [])

  // Convert tenant ID to visual row index (for Handsontable methods)
  const getVisualRowById = useCallback((id) => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return undefined
    const physicalRow = idToRowRef.current.get(id)
    if (physicalRow === undefined) return undefined
    const visualRow = hot.toVisualRow(physicalRow)
    return visualRow !== null ? visualRow : undefined
  }, [])

  // Fetch data from server
  const refetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch(indexUrl, {
        headers: { 'Accept': 'application/json' }
      })
      const result = await response.json()
      const rows = result.data || []

      // The empty-state choice lives in the server-rendered Rent Roll view.
      // Once the final tenant is gone, leave Handsontable and let that branch
      // render instead (including when the deletion arrived from another tab).
      if (response.ok && Array.isArray(result.data) && rows.length === 0) {
        window.location.reload()
        return
      }

      setData(rows)
      setMeta(result.meta || {})

      // Clear pending on refetch
      pendingRef.current.clear()
    } catch (error) {
      console.error('Failed to fetch tenants:', error)
      showError(translations.fetchError || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [indexUrl, translations])

  // Convert raw server data to display format (enum labels, date formatting)
  const displayData = React.useMemo(() => {
    return data.map(convertRowToDisplay)
  }, [data, convertRowToDisplay])

  // Initial load
  useEffect(() => {
    refetchData()
  }, []) // Only on mount

  // Rebuild id→row map after data changes and HotTable applies displayData via prop
  useEffect(() => {
    const hot = hotRef.current?.hotInstance
    if (hot && displayData.length > 0) {
      rebuildIdMap()
    }
  }, [displayData, rebuildIdMap])

  // EVA-138: paste overflow shared with MlaTable — creates missing rows via
  // batch_create, refetches, replays the paste and flushes immediately.
  // Callbacks reference flush (defined later) lazily — they only run after render.
  const { handleBeforePaste, creatingRowsRef } = usePasteOverflow({
    hotRef,
    enabled: !readOnly,
    createRows: async (count) => {
      const response = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          request_id: crypto.randomUUID(),
          items: Array.from({ length: count }, (_, i) => ({ temp_id: `paste_${Date.now()}_${i}`, data: { active: true } }))
        })
      })
      const result = await response.json()
      return result.success === true
    },
    refetchData,
    flushNow: () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      return flush()
    },
    getAnchorId: (row) => hotRef.current?.hotInstance?.getDataAtRowProp(row, 'id'),
    resolveAnchorRow: (id) => getVisualRowById(id)
  })

  // ActionCable subscription
  useEffect(() => {
    subscriptionRef.current = cable.subscriptions.create(
      { channel: 'TenantsChannel', valuation_id: valuationId },
      {
        connected() {
          setConnectionStatus('connected')
          refetchData()
        },
        disconnected() {
          setConnectionStatus('disconnected')
        },
        received(payload) {
          handleServerPushRef.current?.(payload)
        }
      }
    )

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [valuationId])

  // Secondary IndexationProfilesChannel subscription is declared LOWER in the
  // file, after `requestMetaRefresh` is defined (otherwise TDZ would crash
  // the component on first render).

  // Render the connection indicator next to the "Tenants" card title (avoid overlapping action buttons).
  useEffect(() => {
    const addBtn = document.getElementById('add_tenant_btn')
    const cardHeader = addBtn?.closest('.card-header') || document.querySelector('.card-header')
    const titleEl = cardHeader?.querySelector('h5.card-title')
    if (!titleEl) return

    const mount = document.createElement('span')
    mount.className = 'tenants-connection-status-mount'
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)

    return () => {
      setConnectionMountEl(null)
      mount.remove()
    }
  }, [])

  // EVA-153: expose the Handsontable instance + a push hook for end-to-end
  // specs (and dev console debugging). The deferred-push queue ref is also
  // exposed so a spec can observe queuing behavior deterministically.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__evaTenantsHot = hotRef.current?.hotInstance
      window.__evaTenantsPush = (payload) => handleServerPushRef.current?.(payload)
      window.__evaTenantsDeferredQueue = deferredPushQueueRef
    }
    return () => {
      if (typeof window !== 'undefined') {
        if (window.__evaTenantsHot === hotRef.current?.hotInstance) delete window.__evaTenantsHot
        delete window.__evaTenantsPush
        delete window.__evaTenantsDeferredQueue
      }
    }
  }, [])

  // Add tenant row handler (triggered by button click)
  const handleAddTenant = useCallback(async () => {
    if (readOnly) return

    // EVA-178: adding a row triggers a refetch that clears the pending-changes
    // buffer. If the user typed into the previous row and fired the shortcut
    // before the 800ms debounced flush, those edits were wiped ("ghost" data
    // loss). Commit any open editor and flush pending edits FIRST so the
    // create+refetch reloads them from the server instead of discarding them.
    const hot = hotRef.current?.hotInstance
    const editor = hot?.getActiveEditor?.()
    if (editor && typeof editor.isOpened === 'function' && editor.isOpened()) {
      editor.finishEditing()
    }
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    await flushRef.current?.()

    const requestId = crypto.randomUUID()
    const tempId = `temp_${Date.now()}`

    try {
      const response = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          request_id: requestId,
          items: [{
            temp_id: tempId,
            data: { active: true }
          }]
        })
      })

      const result = await response.json()
      
      if (result.success) {
        // Refetch to get new row in correct position
        refetchData()
      } else {
        showError(result.error || 'Failed to create tenant')
      }
    } catch (error) {
      console.error('Error creating tenant:', error)
      showError('Failed to create tenant')
    }
  }, [readOnly, batchCreateUrl, csrfToken, refetchData])

  useEffect(() => {
    window.addEventListener('addTenantRow', handleAddTenant)
    return () => window.removeEventListener('addTenantRow', handleAddTenant)
  }, [handleAddTenant])

  // Normalize values for comparison to avoid unnecessary setDataAtRowProp calls.
  // Handsontable stores numbers natively, but the server sends numeric strings like "60000.0".
  const normalizeForCompare = useCallback((v) => {
    if (v === null || v === undefined || v === '') return ''
    if (typeof v === 'boolean') return v ? '1' : '0'
    const s = String(v)
    const n = Number(s)
    if (!isNaN(n) && s.trim() !== '') return String(n)
    return s
  }, [])

  // EVA-153: Queue of server pushes deferred while a cell editor is open.
  // Each push contains diffs across the row — applying setDataAtRowProp on
  // any cell while another cell's editor is open closes that editor
  // (Handsontable's batch render closes active editors). Defer the push
  // until afterDeselect / afterChange and drain the queue then.
  const deferredPushQueueRef = useRef([])

  // Apply a single push payload. Pulled out of handleServerPush so the
  // queue drain can replay deferred pushes in order.
  const applyServerPush = useCallback((payload) => {
    const { type, ids, changes, request_id, records, temp_id_map } = payload
    const hot = hotRef.current?.hotInstance
    if (!hot) return

    if (type === 'update') {
      // Sync non-Handsontable relation fields into a ref (no setState → no HotTable reload).
      // The Area Schedule side panel reads segments from this cache when it opens.
      ids.forEach(id => {
        const rowChanges = changes[String(id)] || changes[id] || {}
        if ('market_rent_segments' in rowChanges) {
          segmentsCacheRef.current.set(id, rowChanges.market_rent_segments)
        }
        if ('rent_review_inputs' in rowChanges) {
          reviewsCacheRef.current.set(id, rowChanges.rent_review_inputs)
          // Force a re-render of the summary cell — refetch keeps row in sync.
        }

        // EVA-173: merge the raw server values into the cached row as well.
        // The Rent Review modal and MR Breakdown panel build their payload
        // from dataRef; grid ACKs only wrote to Handsontable, so rows edited
        // since the last full refetch opened as "Unnamed Tenant" with blank
        // lease dates until a page refresh. Mutating the ref (no setState)
        // keeps any open cell editor alive (EVA-153).
        const cachedRow = dataRef.current.find(r => r.id === id)
        if (cachedRow) Object.assign(cachedRow, rowChanges)
      })

      ids.forEach(id => {
        const visualRow = getVisualRowById(id)
        if (visualRow === undefined) return

        // Object keys are strings in JS
        const rowChanges = changes[String(id)] || changes[id] || {}

        hot.batch(() => {
          Object.entries(rowChanges).forEach(([field, serverValue]) => {
            // Skip relation fields that aren't Handsontable columns
            if (field === 'market_rent_segments') return


            const pendingKey = `${id}:${field}`
            const pending = pendingRef.current.get(pendingKey)

            // Convert server value to display label for enum fields
            let displayValue = serverValue
            if (ENUM_FIELDS.includes(field) && enumMappings[field]) {
              displayValue = enumMappings[field].valueToLabel[serverValue] || serverValue
            }
            // Skip setDataAtRowProp when value hasn't changed to avoid
            // re-renders that disrupt the active cell editor (EVA-52)
            const currentValue = hot.getDataAtRowProp(visualRow, field)
            const valueChanged = normalizeForCompare(currentValue) !== normalizeForCompare(displayValue)

            if (pending) {
              if (pending.batchRequestId === request_id) {
                // ACK our change — only write if server modified the value
                if (valueChanged) {
                  hot.setDataAtRowProp(visualRow, field, displayValue, SOURCE.SERVER)
                }
                pendingRef.current.delete(pendingKey)
                showCellSuccess(visualRow, field)
              }
              // Else: cell in pending with different requestId -> don't apply
            } else {
              // Cell not in pending -> apply external push only if value differs
              if (valueChanged) {
                hot.setDataAtRowProp(visualRow, field, displayValue, SOURCE.SERVER)
              }
            }
          })
        })
      })
    } else if (type === 'create') {
      // Skip refetch while paste overflow is creating rows — the paste handler
      // already calls refetchData and will replay the pasted values afterwards.
      if (!creatingRowsRef.current) refetchData()
    } else if (type === 'destroy') {
      // Refetch to remove deleted records
      refetchData()
    }
  }, [refetchData, enumMappings, getVisualRowById, normalizeForCompare])

  // Swarm D13: a full refetch (e.g. IndexationProfilesChannel push) swaps
  // the entire data prop and would close any open editor — regression of
  // EVA-153. Set this flag while an editor is open; the same drain hook
  // re-runs refetchData once the editor closes.
  const deferredMetaRefreshRef = useRef(false)

  // EVA-153: defer pushes while a cell editor is open so a setDataAtRowProp
  // on an unrelated cell can't close the editor and lose the user's in-flight
  // input. The next afterDeselect / afterChange drains the queue.
  const handleServerPush = useCallback((payload) => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return
    const editor = hot.getActiveEditor?.()
    if (editor && typeof editor.isOpened === 'function' && editor.isOpened()) {
      deferredPushQueueRef.current.push(payload)
      return
    }
    applyServerPush(payload)
  }, [applyServerPush])

  // Same guard for full-table meta refreshes (dropdown source changes etc).
  const requestMetaRefresh = useCallback(() => {
    const hot = hotRef.current?.hotInstance
    const editor = hot?.getActiveEditor?.()
    if (editor && typeof editor.isOpened === 'function' && editor.isOpened()) {
      deferredMetaRefreshRef.current = true
      return
    }
    refetchData()
  }, [refetchData])

  // Drain any deferred pushes once the editor closes (selection ends).
  useEffect(() => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return undefined

    const drain = () => {
      if (deferredPushQueueRef.current.length > 0) {
        const queue = deferredPushQueueRef.current
        deferredPushQueueRef.current = []
        queue.forEach(applyServerPush)
      }
      if (deferredMetaRefreshRef.current) {
        deferredMetaRefreshRef.current = false
        refetchData()
      }
    }

    hot.addHook('afterDeselect', drain)
    hot.addHook('afterChange', drain)
    hot.addHook('afterDestroyEditor', drain)
    return () => {
      hot.removeHook('afterDeselect', drain)
      hot.removeHook('afterChange', drain)
      hot.removeHook('afterDestroyEditor', drain)
    }
  }, [applyServerPush, refetchData])

  // Refresh dropdown source when indexation profiles are renamed / created /
  // deleted from another tab. Defers the full refetch when a cell editor is
  // open (Swarm D13 — preserves EVA-153 invariant). Placed after
  // `requestMetaRefresh` is defined to avoid a TDZ on first render.
  useEffect(() => {
    if (!valuationId) return
    const sub = cable.subscriptions.create(
      { channel: 'IndexationProfilesChannel', valuation_id: valuationId },
      { received: () => { requestMetaRefresh() } }
    )
    return () => sub.unsubscribe()
  }, [valuationId, requestMetaRefresh])

  // Keep ref in sync with latest handleServerPush
  useEffect(() => {
    handleServerPushRef.current = handleServerPush
  }, [handleServerPush])

  // Show success flash on cell
  const showCellSuccess = useCallback((row, prop) => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return

    const col = columns.findIndex(c => c.data === prop)
    if (col === -1) return

    const td = hot.getCell(row, col)
    if (td) {
      // Clear the pending yellow — setDataAtRowProp may have been skipped
      // (EVA-52) so nothing else re-renders the cell to strip the class.
      td.classList.remove('cell-pending')
      td.classList.add('cell-success')
      setTimeout(() => td.classList.remove('cell-success'), 1500)
    }
  }, [columns])

  // Open MLA modal (like Lease Breaks pattern)
  const openMlaModal = useCallback(async (tenantId) => {
    if (!mlaModalUrlTemplate) return
    const showUrl = mlaModalUrlTemplate.replace(':id', tenantId)

    const loadModal = async (url) => {
      const response = await fetch(url || showUrl, {
        headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      return response.text()
    }

    const mountModal = async (url) => {
      const html = await loadModal(url)
      const container = document.getElementById('mla_modal_container')
      if (!container) return

      // Dispose existing modal before replacing content
      const oldModalEl = container.querySelector('#mlaModal')
      if (oldModalEl) {
        const oldModal = Modal.getInstance(oldModalEl)
        if (oldModal) oldModal.dispose()
      }

      container.innerHTML = html
      const modalEl = document.getElementById('mlaModal')
      if (!modalEl) return

      // Single delegated handler for all form submissions
      modalEl.addEventListener('submit', async (e) => {
        const form = e.target.closest('form')
        if (!form) return
        e.preventDefault()
        const formData = new FormData(form)
        const method = form.querySelector('[name="_method"]')?.value?.toUpperCase() || form.method.toUpperCase()
        try {
          const resp = await fetch(form.action, {
            method: method === 'PATCH' ? 'PATCH' : (method === 'DELETE' ? 'DELETE' : 'POST'),
            headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          })
          // EVA-136: the response is the refreshed modal HTML — swap only the
          // modal-body in place. Remounting the whole modal replayed the fade
          // animation and made the page blink on every Select.
          const html = await resp.text()
          const parsed = new DOMParser().parseFromString(html, 'text/html')
          const fresh = parsed.querySelector('.modal-body') || parsed.body
          const modalBody = modalEl.querySelector('.modal-body')
          if (modalBody && fresh) modalBody.innerHTML = fresh.innerHTML
          await refetchData()
        } catch (err) {
          console.error('MLA modal form error:', err)
        }
      })

      // Navigation links replace modal-body content (Edit, Create, Back)
      modalEl.addEventListener('click', async (e) => {
        const link = e.target.closest('a[data-mla-navigate]')
        if (!link) return
        e.preventDefault()
        try {
          const resp = await fetch(link.href, {
            headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
          })
          const html = await resp.text()
          const modalBody = modalEl.querySelector('.modal-body')
          if (modalBody) {
            modalBody.innerHTML = html
          }
        } catch (err) {
          console.error('MLA modal navigation error:', err)
        }
      })

      // Show modal
      const modal = new Modal(modalEl)
      modal.show()
      modalEl.addEventListener('hidden.bs.modal', () => {
        modal.dispose()
        container.innerHTML = ''
        refetchData()
      }, { once: true })
    }

    try {
      await mountModal()
    } catch (e) {
      console.error('Failed to open MLA modal:', e)
    }
  }, [mlaModalUrlTemplate, refetchData])

  // Flush pending changes to server.
  // Market Rent fields (source/unit/rate/notes) route to /batch_update_market_rent,
  // everything else routes to /batch_update. Both share one request_id so ACK matches.
  const flush = useCallback(async () => {
    const pending = pendingRef.current
    if (pending.size === 0) return

    const batchRequestId = crypto.randomUUID()
    currentBatchIdRef.current = batchRequestId

    // Assign requestId to all pending entries
    pending.forEach((value, key) => {
      pending.set(key, { ...value, batchRequestId })
    })

    const hot = hotRef.current?.hotInstance
    if (!hot) return

    const stdMap = new Map()
    const mrMap = new Map()
    pending.forEach(({ batchRequestId: rid }, key) => {
      if (rid !== batchRequestId) return
      const [idStr, field] = key.split(':')
      const id = parseInt(idStr)
      const visualRow = getVisualRowById(id)
      if (visualRow === undefined) return

      const targetMap = MR_FIELDS.has(field) ? mrMap : stdMap
      if (!targetMap.has(id)) targetMap.set(id, {})
      targetMap.get(id)[field] = hot.getDataAtRowProp(visualRow, field)
    })

    const toItems = (map) => Array.from(map.entries()).map(([id, changes]) => ({
      id,
      changes: convertChangesToServer(changes)
    }))

    const requests = []
    if (stdMap.size > 0) {
      requests.push(fetch(batchUpdateUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ request_id: batchRequestId, items: toItems(stdMap) })
      }).then(r => r.json()))
    }
    if (mrMap.size > 0 && batchUpdateMarketRentUrl) {
      requests.push(fetch(batchUpdateMarketRentUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ request_id: batchRequestId, items: toItems(mrMap) })
      }).then(r => r.json()))
    }

    try {
      const results = await Promise.all(requests)
      results.forEach(result => handleBatchResponse(result, batchRequestId))
    } catch (error) {
      console.error('Batch update failed:', error)
      showError(translations.updateError || 'Failed to save changes')
    }
  }, [batchUpdateUrl, batchUpdateMarketRentUrl, csrfToken, translations, convertChangesToServer, getVisualRowById])

  // Keep flushRef pointing at the latest flush so earlier callbacks
  // (handleAddTenant, EVA-178) can flush pending edits without a TDZ ref.
  useEffect(() => {
    flushRef.current = flush
  }, [flush])

  // Schedule flush with debounce
  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current)
    }
    flushTimeoutRef.current = setTimeout(flush, DEBOUNCE_DELAY)
  }, [flush])

  // Handle batch response
  const handleBatchResponse = useCallback((result, batchRequestId) => {
    if (!result.success) {
      if (result.error === 'validation_failed' && result.errors_by_id) {
        // Show errors per field — fall back to a toast when the field has
        // no visible column or the lookup fails (Swarm D1: prevent silent
        // drops of :base / unknown field errors).
        Object.entries(result.errors_by_id).forEach(([idStr, fieldErrors]) => {
          const id = parseInt(idStr)
          const visualRow = getVisualRowById(id)
          Object.entries(fieldErrors).forEach(([field, messages]) => {
            const message = messages.join(', ')
            const col = columns.findIndex(c => c.data === field)
            if (visualRow !== undefined && col !== -1) {
              showCellError(visualRow, field, message)
            } else {
              showError(message)
            }
          })
        })
      } else {
        showError(result.error || translations.updateError || 'Update failed')
      }
    }
    // Success handling is done via ActionCable push
  }, [translations, getVisualRowById, columns])

  // Show error on cell
  const showCellError = useCallback((row, prop, message) => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return

    const col = columns.findIndex(c => c.data === prop)
    if (col === -1) return

    const td = hot.getCell(row, col)
    if (td) {
      td.classList.add('cell-error')
      td.title = message
    }
  }, [columns])

  // Open lease breaks modal
  // kind: 'BREAK' for lease breaks, 'RENT_FREE' for rent free periods
  const openLeaseBreaksModal = useCallback(async (tenantId, kind = 'BREAK') => {
    if (!leaseBreaksUrlTemplate) return

    const url = leaseBreaksUrlTemplate.replace(':id', tenantId) + `?kind=${kind}`

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const html = await response.text()
      const container = document.getElementById('lease_breaks_modal_container')

      if (container) {
        container.innerHTML = html

        const modalElement = document.getElementById('leaseBreaksModal')
        if (modalElement) {
          const modal = new Modal(modalElement)
          modal.show()

          // Clean up when modal closes (data updates come via ActionCable)
          modalElement.addEventListener('hidden.bs.modal', () => {
            container.innerHTML = ''
          }, { once: true })
        }
      }
    } catch (error) {
      console.error('Error loading lease breaks modal:', error)
      showError(translations.leaseBreaksError || 'Failed to load lease breaks')
    }
  }, [leaseBreaksUrlTemplate, translations])

  // Handle cell changes
  const handleAfterChange = useCallback((changes, source) => {
    if (source === SOURCE.SERVER || source === SOURCE.LOAD || !changes) return

    const hot = hotRef.current?.hotInstance
    if (!hot) return

    changes.forEach(([row, prop, oldVal, newVal]) => {
      if (prop === 'id' || oldVal === newVal) return

      const id = hot.getDataAtRowProp(row, 'id')
      if (!id) return

      const key = `${id}:${prop}`

      // Store oldValue, requestId will be assigned on flush
      pendingRef.current.set(key, { oldValue: oldVal, batchRequestId: null })

      // Mark cell as pending
      const col = columns.findIndex(c => c.data === prop)
      if (col !== -1) {
        const td = hot.getCell(row, col)
        if (td) {
          td.classList.remove('cell-error')
          td.classList.add('cell-pending')
          td.title = ''
        }
      }
    })

    scheduleFlush()
  }, [columns, scheduleFlush])

  // Handle cell mouse down - for lease break cells, MLA info icon, Area Schedule Edit chip
  const handleCellMouseDown = useCallback((event, coords) => {
    if (coords.row < 0) return

    const field = columns[coords.col]?.data
    const hot = hotRef.current?.hotInstance
    if (!hot) return

    // MLA info icon click → open modal
    if (field === 'market_leasing_assumption_id' && event.target?.dataset?.mlaInfo) {
      event.stopImmediatePropagation()
      const tenantId = hot.getDataAtRowProp(coords.row, 'id')
      if (tenantId) openMlaModal(tenantId)
      return
    }

    // Area Schedule Edit chip click → open side panel (AreaScheduleEditor React component)
    if (field === 'market_rent_unit' && event.target?.dataset?.areaScheduleEdit) {
      event.stopImmediatePropagation()
      const tenantId = hot.getDataAtRowProp(coords.row, 'id')
      if (tenantId) {
        const rowData = dataRef.current.find(r => r.id === tenantId) || {}
        const segmentsUrl = marketRentSegmentsUrlTemplate
          ? marketRentSegmentsUrlTemplate.replace(':id', tenantId)
          : ''
        const cachedSegments = segmentsCacheRef.current.get(tenantId)
        const segments = Array.isArray(cachedSegments)
          ? cachedSegments
          : (Array.isArray(rowData.market_rent_segments) ? rowData.market_rent_segments : [])
        window.dispatchEvent(new CustomEvent('openAreaSchedulePanel', {
          detail: {
            tenantId,
            tenantName: rowData.title || '',
            tenantArea: rowData.area ?? 0,
            segments,
            segmentsUrl,
            csrfToken,
            parkingUnitOptions: meta.market_rent_parking_units || [],
            areaUnitOptions: meta.market_rent_units || []
          }
        }))
      }
      return
    }

    // Lease break cells
    if (field === 'lease_break_days' || field === 'rent_free_days') {
      const tenantId = hot.getDataAtRowProp(coords.row, 'id')
      if (tenantId) {
        const kind = field === 'lease_break_days' ? 'BREAK' : 'RENT_FREE'
        openLeaseBreaksModal(tenantId, kind)
      }
      return
    }

    // Rent Reviews cell → open modal
    if (field === 'rent_reviews') {
      const tenantId = hot.getDataAtRowProp(coords.row, 'id')
      if (!tenantId || !rentReviewsUrlTemplate) return
      const rowData = dataRef.current.find(r => r.id === tenantId) || {}
      const reviews = reviewsCacheRef.current.get(tenantId)
        || (Array.isArray(rowData.rent_review_inputs) ? rowData.rent_review_inputs : [])
      window.dispatchEvent(new CustomEvent('openRentReviewsModal', {
        detail: {
          tenant: {
            id: tenantId,
            title: rowData.title || '',
            area: rowData.area,
            lease_start_date: rowData.lease_start_date,
            lease_end_date: rowData.lease_end_date,
            applied_lease_end_date: rowData.applied_lease_end_date,
            payment_schedule: rowData.payment_schedule,
            annual_rent: rowData.annual_rent
          },
          reviews,
          valuationDate: valuationDate || '',
          replaceUrl: rentReviewsUrlTemplate.replace(':id', tenantId),
          requestId: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now())
        }
      }))
    }
  }, [columns, openLeaseBreaksModal, openMlaModal, marketRentSegmentsUrlTemplate, rentReviewsUrlTemplate, valuationDate, csrfToken])

  // Handle row selection (for bulk actions)
  const handleSelectionEnd = useCallback((row, col, row2, col2) => {
    if (readOnly) return

    const hot = hotRef.current?.hotInstance
    if (!hot) return

    // Get all selected ranges
    const selected = hot.getSelected() || []
    const selectedIds = new Set()

    selected.forEach(([startRow, startCol, endRow, endCol]) => {
      const minRow = Math.min(startRow, endRow)
      const maxRow = Math.max(startRow, endRow)

      // A full-row selection (row-header click, or a range spanning all columns)
      // marks a single row for deletion. EVA-165 #3: a selection spanning two or
      // more rows also counts, so the user can just highlight several lines and
      // delete them all at once — no need to grab the row headers, and no more
      // one-row-at-a-time. A single-cell / single-row cell selection is ignored
      // so normal editing doesn't surface the bulk-delete bar.
      const isFullRowSelection = startCol === -1 ||
        (startCol === 0 && endCol === columns.length - 1)
      const spansMultipleRows = maxRow > minRow

      if ((isFullRowSelection || spansMultipleRows) && startRow >= 0) {
        for (let r = minRow; r <= maxRow; r++) {
          const id = hot.getDataAtRowProp(r, 'id')
          if (id && !String(id).startsWith('temp_')) {
            selectedIds.add(id)
          }
        }
      }
    })

    const newSelectedIds = Array.from(selectedIds)

    // Only update if selection changed (prevent infinite loop)
    setSelectedRowIds(prev => {
      if (prev.length !== newSelectedIds.length) return newSelectedIds
      if (prev.some((id, i) => id !== newSelectedIds[i])) return newSelectedIds
      return prev
    })

  }, [readOnly, columns.length])

  // Handle deselection
  const handleDeselect = useCallback(() => {
    setSelectedRowIds(prev => prev.length > 0 ? [] : prev)
  }, [])

  // Keep header (nested headers) frozen by giving Handsontable a fixed height (px).
  // We measure a CSS-sized viewport (e.g. 70vh) and pass the numeric height to HotTable.
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const update = () => {
      const next = el.clientHeight
      setHotHeight(prev => (prev === next ? prev : next))
      hotRef.current?.hotInstance?.refreshDimensions?.()
    }

    update()

    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update())
      ro.observe(el)
    } else {
      window.addEventListener('resize', update)
    }

    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener('resize', update)
    }
  }, [])

  // Bulk delete selected rows
  const handleBulkDelete = useCallback(async () => {
    if (selectedRowIds.length === 0) return

    const confirmMessage = translations.bulkDeleteConfirm || 
      `Are you sure you want to delete ${selectedRowIds.length} tenant(s)?`
    const confirmed = window.confirm(confirmMessage)
    if (!confirmed) return

    const requestId = crypto.randomUUID()

    try {
      const response = await fetch(batchDestroyUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          request_id: requestId,
          ids: selectedRowIds
        })
      })

      const result = await response.json()

      if (result.success) {
        setSelectedRowIds([])
        // Clear selection in Handsontable
        const hot = hotRef.current?.hotInstance
        if (hot) hot.deselectCell()
        // Data will be updated via ActionCable, but refetch for immediate feedback
        refetchData()
      } else {
        showError(result.error || 'Failed to delete tenants')
      }
    } catch (error) {
      console.error('Error deleting tenants:', error)
      showError('Failed to delete tenants')
    }
  }, [selectedRowIds, batchDestroyUrl, csrfToken, translations, refetchData])

  // Block sorting when pending changes exist
  const handleBeforeColumnSort = useCallback((currentSortConfig, destinationSortConfigs) => {
    if (pendingRef.current.size > 0) {
      showError(translations.savePendingFirst || 'Save pending changes before sorting')
      return false
    }
    return true
  }, [translations])

  // Normalize numeric values on paste/edit (EVA-50: comma thousand vs decimal separator)
  const handleBeforeChange = useCallback((changes, source) => {
    if (!changes || source === SOURCE.SERVER || source === SOURCE.LOAD) return

    changes.forEach((change) => {
      const prop = change[1]
      const newVal = change[3]
      if (NUMERIC_PROPS.has(prop) && typeof newVal === 'string') {
        change[3] = normalizeNumericValue(newVal)
      }
      if (DATE_FIELDS.includes(prop) && newVal !== null && newVal !== undefined && newVal !== '') {
        change[3] = parseDisplayDateToISO(newVal)
      }
    })
  }, [parseDisplayDateToISO])

  // Intercept paste that overflows existing rows: cancel, pre-create rows, then replay

  const handleKeyboardShortcut = useCallback((event) => {
    if (readOnly) return
    handleHotKeyboardShortcut(event, {
      onAddRow: () => window.dispatchEvent(new CustomEvent('addTenantRow')),
      onDeleteRow: () => { if (selectedRowIds.length > 0) handleBulkDelete() }
    })
  }, [readOnly, selectedRowIds, handleBulkDelete])

  // EVA-165 (2/3): double-click on the fill handle fills the selected cell's
  // value down. Two strategies:
  //   * plain double-click — fills EMPTY cells until the first occupied one
  //     (Excel-style, safe default for populating a fresh rent roll);
  //   * Shift + double-click — overwrites everything down to the last data
  //     row (e.g. switching Rent Unit for all 247 tenants), which the native
  //     autofill refuses to do across occupied cells.
  // Dragging the handle still works for short ranges; double-click covers
  // the long ones where dragging fights the scroll.
  //
  // Dynamic per-cell readOnly is assigned inside renderers (EVA-110), so the
  // meta is stale for rows that were never rendered — replicate those checks
  // from the row data instead of trusting getCellMeta alone.
  const fillGuardByProp = useMemo(() => ({
    current_rent: (hot, row) =>
      hot.getDataAtRowProp(row, 'current_rent_unit') !== (enumMappings.current_rent_unit?.valueToLabel?.MARKET_RENT || 'Market Rent'),
    market_rent_unit: (hot, row) => hot.getDataAtRowProp(row, 'market_rent_source') === 'Manual',
    market_rent_rate: (hot, row) => hot.getDataAtRowProp(row, 'market_rent_source') === 'Manual'
  }), [enumMappings])

  const handleCornerDblClick = useCallback((event) => {
    if (readOnly) return
    const hot = hotRef.current?.hotInstance
    if (!hot) return
    const sel = hot.getSelectedRangeLast()
    if (!sel) return

    const overwrite = !!event?.shiftKey
    const top = Math.max(0, Math.min(sel.from.row, sel.to.row))
    const colStart = Math.max(0, Math.min(sel.from.col, sel.to.col))
    const colEnd = Math.max(sel.from.col, sel.to.col)

    let lastDataRow = -1
    for (let row = hot.countRows() - 1; row >= 0; row -= 1) {
      if (hot.getDataAtRowProp(row, 'id')) { lastDataRow = row; break }
    }
    if (lastDataRow <= top) return

    const isEmpty = (value) => value === null || value === undefined || String(value).trim() === ''

    const changes = []
    for (let col = colStart; col <= colEnd; col += 1) {
      const prop = hot.colToProp(col)
      const guard = fillGuardByProp[prop]
      // Column-level readOnly (lease breaks, annual rent, …) never fills.
      if (hot.getCellMeta(top, col).readOnly && !guard) continue

      const value = hot.getDataAtCell(top, col)
      for (let row = top + 1; row <= lastDataRow; row += 1) {
        if (!hot.getDataAtRowProp(row, 'id')) continue
        const blocked = guard ? !guard(hot, row) : hot.getCellMeta(row, col).readOnly

        if (overwrite) {
          if (blocked) continue
          changes.push([row, col, value])
        } else {
          // Safe mode stops at the first cell it cannot or should not fill —
          // occupied or blocked — so each column fills its contiguous gap.
          if (blocked || !isEmpty(hot.getDataAtCell(row, col))) break
          changes.push([row, col, value])
        }
      }
    }
    if (changes.length) hot.setDataAtCell(changes, SOURCE.EDIT)
  }, [readOnly, fillGuardByProp])

  // EVA-132: the right-click menu keeps only Copy/Cut. The built-in row
  // insert/remove items never persisted (Handsontable's row ops aren't wired to
  // the tenant create/delete backend — adding/removing tenants is done via the
  // "Add Tenant" button and row delete), and the "Apply Market Rent … to
  // selection" actions are no longer needed.
  const contextMenuConfig = useMemo(() => {
    if (readOnly) return false
    return {
      items: {
        'copy': {},
        'cut': {}
      }
    }
  }, [readOnly])

  // Global readOnly fallback; per-cell MR readOnly toggling happens in the renderers
  // because cells() results are memoized by Handsontable until forceFullRender.
  const cells = useCallback(() => {
    return readOnly ? { readOnly: true } : {}
  }, [readOnly])

  return (
    <div className="tenants-table-container">
      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: translations.connected || 'Connected',
          disconnected: translations.disconnected || 'Disconnected',
          connecting: translations.connecting || 'Connecting...',
        }}
      />

      {loading && (
        <div className="loading-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div ref={viewportRef} className="tenants-table-viewport">
        <HotTable
          ref={hotRef}
          data={displayData}
          columns={columns}
          colWidths={colWidths}
          colHeaders={false}
          nestedHeaders={nestedHeaders}
          // Enable collapse/expand buttons on grouped headers (those with `colspan`).
          // Works with NestedHeaders plugin and our top "General / Lease Dates / Rental Income" groups.
          collapsibleColumns={true}
          rowHeaders={true}
          height={hotHeight || undefined}
          fixedColumnsStart={3}
          themeName="ht-theme-horizon"
          licenseKey={HOT_LICENSE_KEY}
          sanitizer={sanitizeHandsontableHtml}
          stretchH="none"
          dropdownMenu={true}
          filters={true}
          wordWrap={false}
          autoRowSize={false}
          rowHeights={36}
          columnHeaderHeight={[28, 52]}
          columnSorting={{
            // EVA-165: default to the server's id / insertion order (stable)
            // instead of re-sorting by suite. The suite order diverged from the
            // paste/insertion order, which (together with transient state during
            // a large paste) made rows appear to "jump". Users can still sort by
            // clicking a column header.
            indicator: true,
            headerAction: true
          }}
          manualColumnResize={true}
          manualRowResize={true}
          contextMenu={contextMenuConfig}
          beforeChange={handleBeforeChange}
          afterChange={handleAfterChange}
          afterOnCellMouseDown={handleCellMouseDown}
          afterSelectionEnd={handleSelectionEnd}
          afterDeselect={handleDeselect}
          afterGetColHeader={handleAfterGetColHeader}
          afterGetRowHeader={handleAfterGetRowHeader}
          beforeColumnSort={handleBeforeColumnSort}
          beforePaste={handleBeforePaste}
          afterOnCellCornerDblClick={handleCornerDblClick}
          afterDocumentKeyDown={handleKeyboardShortcut}
          cells={cells}
          className="tenants-hot-table"
        />
      </div>

      {/* Bulk actions overlay */}
      {selectedRowIds.length > 0 && !readOnly && (
        <div className="bulk-actions-overlay">
          <div className="d-flex align-items-center justify-content-between">
            <span>
              {translations.selected || 'Selected'}: <strong>{selectedRowIds.length}</strong>
            </span>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleBulkDelete()
              }}
            >
              <i className="bi bi-trash me-1"></i>
              {translations.delete || 'Delete'}
            </button>
          </div>
        </div>
      )}

    </div>
  )
}
